import type React from 'react';
import { Check, Undo2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { AiSuggestion } from '@/lib/api';
import { ArtifactBodyRenderer } from './ArtifactBodyRenderer';

interface ArtifactDraftPreviewProps {
  suggestion: AiSuggestion;
  accepting: boolean;
  rejecting: boolean;
  disabled: boolean;
  onAccept: () => void;
  onReject: () => void;
  onBack: () => void;
}

/**
 * Full-document draft preview shown in the main artifact body area when a
 * user clicks "Open" on an AI suggestion. Renders the proposed Tiptap JSON
 * read-only with a persistent banner exposing Accept / Reject / Back to
 * current actions.
 */
export function ArtifactDraftPreview({
  suggestion,
  accepting,
  rejecting,
  disabled,
  onAccept,
  onReject,
  onBack,
}: ArtifactDraftPreviewProps): React.ReactElement {
  const busy = accepting || rejecting;
  return (
    <div className="flex flex-col gap-2">
      <div
        role="region"
        aria-label="AI draft preview"
        className="flex flex-wrap items-center gap-2 rounded-md border border-warning/30 bg-warning/15 p-2 text-xs text-warning"
      >
        <span className="font-semibold uppercase tracking-wide">AI draft</span>
        {suggestion.provider && <Badge variant="outline">{suggestion.provider}</Badge>}
        {suggestion.model && <Badge variant="secondary">{suggestion.model}</Badge>}
        <span className="text-warning/80">·</span>
        <span className="truncate">{suggestion.summary ?? draftKindLabel(suggestion.kind)}</span>
        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={onBack}
            className="inline-flex gap-1.5"
          >
            <Undo2 className="size-4" />
            Back to current
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled || busy}
            onClick={onReject}
            className="inline-flex gap-1.5"
          >
            <X className="size-4" />
            {rejecting ? 'Rejecting…' : 'Reject'}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={disabled || busy}
            onClick={onAccept}
            className="inline-flex gap-1.5"
          >
            <Check className="size-4" />
            {accepting ? 'Accepting…' : 'Accept'}
          </Button>
        </div>
      </div>
      {suggestion.kind !== 'generate_full' && (
        <div className="rounded-md border border-border bg-surface/40 px-2 py-1 text-xs text-text-secondary">
          {scopedDraftDescription(suggestion)}
          {/* TODO: replace with proper inline diff (insertions green, deletions red). */}
        </div>
      )}
      <ArtifactBodyRenderer
        json={suggestion.proposedRichTextJson}
        plainText={suggestion.proposedMarkdown}
        ariaLabel="AI draft body"
      />
    </div>
  );
}

function draftKindLabel(kind: AiSuggestion['kind']): string {
  switch (kind) {
    case 'generate_full':
      return 'Generated document';
    case 'generate_section':
      return 'Generated section';
    case 'suggest_replace':
      return 'Replace suggestion';
    case 'suggest_insert':
      return 'Insert suggestion';
  }
}

function scopedDraftDescription(suggestion: AiSuggestion): string {
  const intent = suggestion.targetIntent;
  switch (intent.kind) {
    case 'document':
      return 'Full-document replacement';
    case 'section':
      return `Section replacement: ${intent.headingPath.join(' / ') || '(section)'}`;
    case 'replace':
      return `Block replacement at index ${String(intent.anchorBlockIndex)}`;
    case 'insert':
      return `Insert after block index ${String(intent.afterBlockIndex)}`;
  }
}
